import clsx from 'clsx';
import { AlertCircle, CheckCircle2, Info, Loader2, X, XCircle } from 'lucide-react';
import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';

// ── Button ───────────────────────────────────────────────────────────────────

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
type ButtonSize = 'sm' | 'md' | 'lg';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand-600 text-white hover:bg-brand-500 active:bg-brand-700 shadow-lg shadow-brand-900/30',
  secondary: 'bg-surface-overlay text-slate-200 hover:bg-surface-hover border border-surface-border',
  ghost: 'text-slate-400 hover:text-slate-100 hover:bg-surface-hover',
  danger: 'bg-red-600 text-white hover:bg-red-500 active:bg-red-700',
  success: 'bg-emerald-600 text-white hover:bg-emerald-500 active:bg-emerald-700',
};

const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
  lg: 'h-12 px-6 text-base gap-2',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  className,
  children,
  disabled,
  ...props
}: ButtonProps) {
  return (
    <button
      className={clsx(
        'inline-flex items-center justify-center rounded-lg font-medium transition-all',
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none',
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        className,
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : icon}
      {children}
    </button>
  );
}

// ── Card ─────────────────────────────────────────────────────────────────────

export function Card({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={clsx('card', className)} {...props}>
      {children}
    </div>
  );
}

export function CardHeader({
  title,
  description,
  action,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={clsx('flex items-start justify-between gap-4 border-b border-surface-border px-5 py-4', className)}>
      <div className="min-w-0">
        <h2 className="text-sm font-semibold text-slate-100">{title}</h2>
        {description ? <p className="mt-1 text-xs text-slate-500">{description}</p> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

// ── Form fields ──────────────────────────────────────────────────────────────

export interface FieldProps {
  label: string;
  error?: string;
  hint?: ReactNode;
  required?: boolean;
  children: ReactNode;
  htmlFor?: string;
}

export function Field({ label, error, hint, required, children, htmlFor }: FieldProps) {
  return (
    <div>
      <label className="label-base" htmlFor={htmlFor}>
        {label}
        {required ? <span className="ml-0.5 text-red-400">*</span> : null}
      </label>
      {children}
      {error ? (
        <p className="mt-1.5 flex items-start gap-1.5 text-xs text-red-400">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>{error}</span>
        </p>
      ) : hint ? (
        <p className="mt-1.5 text-xs text-slate-500">{hint}</p>
      ) : null}
    </div>
  );
}

/** forwardRef so callers can focus/select the field (the OTP screen does). */
export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  ({ className, ...props }, ref) => <input ref={ref} className={clsx('input-base', className)} {...props} />,
);
Input.displayName = 'Input';

export const Select = ({ className, children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) => (
  <select className={clsx('input-base cursor-pointer', className)} {...props}>
    {children}
  </select>
);

export function Toggle({
  checked,
  onChange,
  label,
  description,
  disabled,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
  description?: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="flex w-full items-start gap-3 text-left disabled:opacity-50"
    >
      <span
        className={clsx(
          'mt-0.5 flex h-5 w-9 shrink-0 items-center rounded-full p-0.5 transition-colors',
          checked ? 'bg-brand-500' : 'bg-slate-700',
        )}
      >
        <span
          className={clsx(
            'h-4 w-4 rounded-full bg-white shadow transition-transform',
            checked && 'translate-x-4',
          )}
        />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-medium text-slate-200">{label}</span>
        {description ? <span className="mt-0.5 block text-xs text-slate-500">{description}</span> : null}
      </span>
    </button>
  );
}

// ── Dropdowns ────────────────────────────────────────────────────────────────

/**
 * Closes a popover when the user interacts outside it.
 *
 * Replaces the `onBlur` + `setTimeout` pattern, which loses clicks: blurring
 * the trigger schedules the menu to unmount, and if the pointer is held down
 * for longer than the timeout — or the browser reorders blur and click — the
 * menu is gone before `click` fires and the item silently does nothing. That
 * is why Sign out and Account settings appeared dead.
 *
 * Listening for `pointerdown` on the document and checking containment has no
 * such race: the menu is still mounted when the click lands on it.
 */
export function useDismissOnOutsideClick<T extends HTMLElement>(
  open: boolean,
  onDismiss: () => void,
) {
  const ref = useRef<T>(null);
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) onDismissRef.current();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDismissRef.current();
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  return ref;
}

// ── Badge ────────────────────────────────────────────────────────────────────

type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'brand';

const BADGE_TONES: Record<BadgeTone, string> = {
  neutral: 'bg-slate-800 text-slate-300 ring-slate-700',
  success: 'bg-emerald-500/10 text-emerald-300 ring-emerald-500/30',
  warning: 'bg-amber-500/10 text-amber-300 ring-amber-500/30',
  danger: 'bg-red-500/10 text-red-300 ring-red-500/30',
  info: 'bg-sky-500/10 text-sky-300 ring-sky-500/30',
  brand: 'bg-brand-500/10 text-brand-300 ring-brand-500/30',
};

export function Badge({
  tone = 'neutral',
  children,
  className,
}: {
  tone?: BadgeTone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
        BADGE_TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/** Maps every server-side status enum onto a consistent colour. */
export function statusTone(status: string): BadgeTone {
  switch (status) {
    case 'SUCCESS':
    case 'OK':
    case 'ACTIVE':
      return 'success';
    case 'FAILED':
    case 'AUTH_FAILED':
    case 'CONNECTION_ERROR':
    case 'SUSPENDED':
      return 'danger';
    case 'PENDING':
    case 'IN_PROGRESS':
    case 'SYNCING':
    case 'PENDING_ACTIVATION':
      return 'info';
    case 'SKIPPED':
    case 'DISABLED':
    case 'NEVER_SYNCED':
      return 'neutral';
    default:
      return 'neutral';
  }
}

export function humanizeStatus(status: string): string {
  return status.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}

// ── Alert ────────────────────────────────────────────────────────────────────

const ALERT_STYLES = {
  error: { wrap: 'border-red-500/30 bg-red-500/5 text-red-200', Icon: XCircle, iconClass: 'text-red-400' },
  warning: { wrap: 'border-amber-500/30 bg-amber-500/5 text-amber-200', Icon: AlertCircle, iconClass: 'text-amber-400' },
  success: { wrap: 'border-emerald-500/30 bg-emerald-500/5 text-emerald-200', Icon: CheckCircle2, iconClass: 'text-emerald-400' },
  info: { wrap: 'border-sky-500/30 bg-sky-500/5 text-sky-200', Icon: Info, iconClass: 'text-sky-400' },
} as const;

export function Alert({
  severity = 'info',
  title,
  children,
  action,
  className,
}: {
  severity?: keyof typeof ALERT_STYLES;
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  const { wrap, Icon, iconClass } = ALERT_STYLES[severity];
  return (
    <div className={clsx('flex items-start gap-3 rounded-lg border px-4 py-3 text-sm', wrap, className)} role="alert">
      <Icon className={clsx('mt-0.5 h-4 w-4 shrink-0', iconClass)} aria-hidden />
      <div className="min-w-0 flex-1">
        {title ? <p className="font-medium">{title}</p> : null}
        {children ? <div className={clsx('text-sm opacity-90', title && 'mt-1')}>{children}</div> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

// ── Modal ────────────────────────────────────────────────────────────────────

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}) {
  const panelRef = useRef<HTMLDivElement>(null);

  /**
   * `onClose` is almost always an inline arrow at the call site, so its
   * identity changes on every parent render. Reading it through a ref keeps
   * the effect below depending on `open` alone.
   *
   * This is not a micro-optimisation. With `onClose` in the dependency array,
   * the effect re-ran on every keystroke — and it calls `.focus()` on the
   * panel, which yanked focus out of whatever input the user was typing in.
   * Every letter had to be followed by clicking back into the field.
   */
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKeyDown);

    // Freeze the page behind the dialog so scrolling doesn't bleed through.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Move focus into the dialog once, on open. If the content declares its
    // own autoFocus field, leave that alone — stealing it back to the panel
    // would defeat the point of marking it.
    const panel = panelRef.current;
    if (panel && !panel.contains(document.activeElement)) {
      const firstField = panel.querySelector<HTMLElement>(
        'input:not([type="hidden"]), textarea, select',
      );
      (firstField ?? panel).focus();
    }

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open) return null;

  const widths = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:items-center">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm animate-fade-in" onClick={onClose} aria-hidden />
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        className={clsx(
          'relative z-10 w-full animate-slide-up rounded-2xl border border-surface-border bg-surface-raised shadow-2xl',
          widths[size],
        )}
      >
        <div className="flex items-start justify-between gap-4 border-b border-surface-border px-6 py-4">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-slate-100">{title}</h2>
            {description ? <p className="mt-1 text-sm text-slate-500">{description}</p> : null}
          </div>
          <button
            onClick={onClose}
            className="-mr-2 -mt-1 rounded-lg p-2 text-slate-500 transition hover:bg-surface-hover hover:text-slate-200"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-6 py-5">{children}</div>
        {footer ? (
          <div className="flex items-center justify-end gap-3 border-t border-surface-border px-6 py-4">{footer}</div>
        ) : null}
      </div>
    </div>
  );
}

// ── Toasts ───────────────────────────────────────────────────────────────────

interface Toast {
  id: number;
  severity: keyof typeof ALERT_STYLES;
  message: string;
}

const ToastContext = createContext<{ push: (severity: Toast['severity'], message: string) => void } | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(0);

  const push = useCallback((severity: Toast['severity'], message: string) => {
    const id = (nextId.current += 1);
    setToasts((prev) => [...prev, { id, severity, message }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 6000);
  }, []);

  const value = useMemo(() => ({ push }), [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-full max-w-sm flex-col gap-2">
        {toasts.map((toast) => (
          <div key={toast.id} className="pointer-events-auto animate-slide-up">
            <Alert
              severity={toast.severity}
              className="bg-surface-raised shadow-xl"
              action={
                <button
                  onClick={() => setToasts((prev) => prev.filter((t) => t.id !== toast.id))}
                  className="rounded p-1 opacity-60 transition hover:opacity-100"
                  aria-label="Dismiss"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              }
            >
              {toast.message}
            </Alert>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

// ── States ───────────────────────────────────────────────────────────────────

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
      {icon ? <div className="mb-4 rounded-full bg-surface-overlay p-4 text-slate-500">{icon}</div> : null}
      <h3 className="text-sm font-semibold text-slate-200">{title}</h3>
      {description ? <p className="mt-1.5 max-w-sm text-sm text-slate-500">{description}</p> : null}
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={clsx('h-5 w-5 animate-spin text-brand-400', className)} aria-label="Loading" />;
}

export function TableSkeleton({ rows = 5, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className="space-y-2 p-4">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-3">
          {Array.from({ length: columns }).map((_, c) => (
            <div key={c} className="skeleton h-8 flex-1" style={{ animationDelay: `${(r * columns + c) * 40}ms` }} />
          ))}
        </div>
      ))}
    </div>
  );
}
